/**
 * Agape Energy Theme — shared storefront behaviors
 */
(function () {
  'use strict';

  const CYCLE_SECS = 14 * 3600;
  const RESET_POINTS = [0, 4 * 3600, 8 * 3600, 12 * 3600, 16 * 3600, 20 * 3600];

  function getESTSecondsRemaining() {
    const EST_OFFSET_MS = -5 * 3600 * 1000;
    const nowEst = new Date(Date.now() + EST_OFFSET_MS);
    const secsSinceMidnight =
      nowEst.getUTCHours() * 3600 +
      nowEst.getUTCMinutes() * 60 +
      nowEst.getUTCSeconds();

    let lastReset = 0;
    for (const point of RESET_POINTS) {
      if (secsSinceMidnight >= point) lastReset = point;
    }

    const elapsed = secsSinceMidnight - lastReset;
    return Math.max(0, CYCLE_SECS - elapsed);
  }

  function pad(n) {
    return String(n).padStart(2, '0');
  }

  function updateCountdowns() {
    const seconds = getESTSecondsRemaining();
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    document.querySelectorAll('[data-countdown]').forEach((el) => {
      const format = el.getAttribute('data-countdown');
      if (format === 'columns') {
        const hrsEl = el.querySelector('[data-countdown-hrs]');
        const minsEl = el.querySelector('[data-countdown-mins]');
        const secsEl = el.querySelector('[data-countdown-secs]');
        if (hrsEl) hrsEl.textContent = pad(hrs);
        if (minsEl) minsEl.textContent = pad(mins);
        if (secsEl) secsEl.textContent = pad(secs);
      } else if (format === 'inline') {
        el.textContent = `${pad(hrs)}:${pad(mins)}:${pad(secs)}`;
      }
    });
  }

  function initCountdown() {
    if (!document.querySelector('[data-countdown]')) return;
    updateCountdowns();
    setInterval(updateCountdowns, 1000);
  }

  function initAutoHideHeader() {
    const headerSection = document.querySelector('.header-section');
    const siteHeader = document.querySelector('[data-site-header]');
    if (!headerSection || !siteHeader) return;

    if (window.getComputedStyle(siteHeader).display === 'none') return;

    const announcementBar = document.querySelector('[data-announcement-bar]');
    const mobileNav = document.querySelector('[data-mobile-nav]');
    let lastScrollY = window.scrollY;
    let ticking = false;
    const SCROLL_DELTA = 6;
    const MIN_SCROLL = 72;

    function measureHeaderOffset() {
      const announcementHeight = announcementBar ? announcementBar.offsetHeight : 0;
      const headerHeight = siteHeader.offsetHeight;
      const total = announcementHeight + headerHeight;

      document.documentElement.style.setProperty('--announcement-bar-height', `${announcementHeight}px`);
      document.documentElement.style.setProperty('--site-header-offset', `${total}px`);
    }

    function setHeaderHidden(hidden) {
      document.body.classList.toggle('site-header-hidden', hidden);
    }

    function updateHeaderVisibility() {
      const scrollY = window.scrollY;
      const delta = scrollY - lastScrollY;
      const navOpen = mobileNav && mobileNav.classList.contains('open');

      if (navOpen || scrollY <= 0) {
        setHeaderHidden(false);
      } else if (delta > SCROLL_DELTA && scrollY > MIN_SCROLL) {
        setHeaderHidden(true);
      } else if (delta < -SCROLL_DELTA) {
        setHeaderHidden(false);
      }

      lastScrollY = scrollY;
      ticking = false;
    }

    measureHeaderOffset();
    document.body.classList.add('has-site-header');

    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          requestAnimationFrame(updateHeaderVisibility);
          ticking = true;
        }
      },
      { passive: true }
    );

    window.addEventListener('resize', measureHeaderOffset, { passive: true });
    window.addEventListener('orientationchange', measureHeaderOffset, { passive: true });
  }

  function initDesktopNavDropdown() {
    const dropdowns = document.querySelectorAll('[data-header-dropdown]');
    if (!dropdowns.length) return;

    dropdowns.forEach((dropdown) => {
      const toggle = dropdown.querySelector('[data-header-dropdown-toggle]');
      if (!toggle) return;

      toggle.addEventListener('click', (event) => {
        event.stopPropagation();
        const isOpen = dropdown.classList.contains('is-open');

        dropdowns.forEach((item) => {
          item.classList.remove('is-open');
          const itemToggle = item.querySelector('[data-header-dropdown-toggle]');
          if (itemToggle) itemToggle.setAttribute('aria-expanded', 'false');
        });

        if (!isOpen) {
          dropdown.classList.add('is-open');
          toggle.setAttribute('aria-expanded', 'true');
        }
      });
    });

    document.addEventListener('click', () => {
      dropdowns.forEach((dropdown) => {
        dropdown.classList.remove('is-open');
        const toggle = dropdown.querySelector('[data-header-dropdown-toggle]');
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;

      dropdowns.forEach((dropdown) => {
        dropdown.classList.remove('is-open');
        const toggle = dropdown.querySelector('[data-header-dropdown-toggle]');
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
      });
    });
  }

  function initMobileNav() {
    const openBtn = document.querySelector('[data-mobile-nav-open]');
    const closeBtn = document.querySelector('[data-mobile-nav-close]');
    const overlay = document.querySelector('[data-mobile-nav]');

    if (!openBtn || !overlay) return;

    openBtn.addEventListener('click', () => {
      document.body.classList.remove('site-header-hidden');
      document.body.classList.add('mobile-nav-open');
      overlay.classList.add('open');
      document.body.style.overflow = 'hidden';
    });

    const close = () => {
      overlay.classList.remove('open');
      document.body.classList.remove('mobile-nav-open');
      document.body.style.overflow = '';
    };

    if (closeBtn) closeBtn.addEventListener('click', close);

    overlay.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', close);
    });
  }

  function initAnnouncementModal() {
    const bar = document.querySelector('[data-announcement-bar]');
    const modal = document.querySelector('[data-announcement-modal]');
    if (!bar || !modal) return;

    const STORAGE_DISMISSED = 'agape-announcement-dismissed';
    const STORAGE_SUBMITTED = 'agape-announcement-submitted';
    const AUTO_OPEN_DELAY_MS = 3000;

    if (modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }

    const closeBtn = modal.querySelector('[data-modal-close]');
    const form = modal.querySelector('#AnnouncementForm');
    const success = modal.querySelector('[data-announcement-success]');
    const formContent = modal.querySelector('[data-announcement-form-content]');
    const errorEl = modal.querySelector('[data-announcement-error]');
    const hcaptchaContainer = form ? form.querySelector('[data-hcaptcha-container]') : null;
    const HCAPTCHA_SITEKEY = '2e7f6342-57df-422a-8431-ddd86df296bc';
    let autoOpenTimer = null;
    let hcaptchaWidgetId = null;

    const waitForHcaptcha = () =>
      new Promise((resolve) => {
        if (window.hcaptcha) {
          resolve(window.hcaptcha);
          return;
        }
        const interval = setInterval(() => {
          if (window.hcaptcha) {
            clearInterval(interval);
            resolve(window.hcaptcha);
          }
        }, 100);
      });

    const getHcaptchaWidgetId = async () => {
      if (!hcaptchaContainer) return null;
      if (hcaptchaWidgetId !== null) return hcaptchaWidgetId;
      const hcaptcha = await waitForHcaptcha();
      hcaptchaWidgetId = hcaptcha.render(hcaptchaContainer, {
        sitekey: HCAPTCHA_SITEKEY,
        size: 'invisible',
      });
      return hcaptchaWidgetId;
    };

    const shouldSuppressAutoOpen = () =>
      getActiveDiscountCode() ||
      sessionStorage.getItem(STORAGE_DISMISSED) === 'true' ||
      sessionStorage.getItem(STORAGE_SUBMITTED) === 'true';

    const open = () => {
      modal.classList.add('open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    };

    const close = (persistDismissed = true) => {
      modal.classList.remove('open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (persistDismissed) {
        sessionStorage.setItem(STORAGE_DISMISSED, 'true');
      }
    };

    let autoCloseTimer = null;

    const showSuccess = () => {
      if (errorEl) errorEl.hidden = true;
      if (success) success.hidden = false;
      if (formContent) formContent.hidden = true;
      sessionStorage.setItem(STORAGE_SUBMITTED, 'true');
      if (autoCloseTimer) clearTimeout(autoCloseTimer);
      autoCloseTimer = setTimeout(() => close(false), 2000);
    };

    const revertToForm = () => {
      if (autoCloseTimer) {
        clearTimeout(autoCloseTimer);
        autoCloseTimer = null;
      }
      sessionStorage.removeItem(STORAGE_SUBMITTED);
      if (success) success.hidden = true;
      if (formContent) formContent.hidden = false;
      if (!modal.classList.contains('open')) open();
    };

    let toastTimer = null;
    const getToastEl = () => {
      let toastEl = document.querySelector('[data-agape-toast]');
      if (!toastEl) {
        toastEl = document.createElement('div');
        toastEl.setAttribute('data-agape-toast', '');
        toastEl.className = 'agape-toast';
        toastEl.setAttribute('role', 'alert');
        document.body.appendChild(toastEl);
      }
      return toastEl;
    };

    const showToast = (message) => {
      const toastEl = getToastEl();
      toastEl.textContent = message;
      toastEl.classList.add('visible');
      if (toastTimer) clearTimeout(toastTimer);
      toastTimer = setTimeout(() => toastEl.classList.remove('visible'), 4000);
    };

    const scheduleAutoOpen = () => {
      if (shouldSuppressAutoOpen()) return;

      if (autoOpenTimer) clearTimeout(autoOpenTimer);
      autoOpenTimer = setTimeout(() => {
        if (!shouldSuppressAutoOpen() && !modal.classList.contains('open')) {
          open();
        }
      }, AUTO_OPEN_DELAY_MS);
    };

    if (!shouldSuppressAutoOpen()) {
      if (document.body.dataset.heroIntroComplete === 'true') {
        scheduleAutoOpen();
      } else {
        document.addEventListener('agape:hero-intro-complete', scheduleAutoOpen, { once: true });
      }
    }

    bar.addEventListener('click', open);
    bar.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        open();
      }
    });
    if (closeBtn) closeBtn.addEventListener('click', () => close(true));

    modal.addEventListener('click', (e) => {
      if (e.target === modal) close(true);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && modal.classList.contains('open')) {
        close(true);
      }
    });

    if (form) {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        if (errorEl) errorEl.hidden = true;

        const firstNameValue = form.first_name.value;
        const emailValue = form.email.value;

        showSuccess();

        (async () => {
          let widgetId = null;
          try {
            widgetId = await getHcaptchaWidgetId();
            const hcaptcha = window.hcaptcha;
            const { response: hCaptchaToken } =
              widgetId !== null ? await hcaptcha.execute(widgetId, { async: true }) : { response: null };

            const payload = {
              shopify_domain: form.dataset.shopDomain,
              h_captcha_response: hCaptchaToken,
              form_instance_id: form.dataset.formInstanceId,
              first_name: firstNameValue,
              email: emailValue,
              customer_consented_to_email_marketing: true,
              customer_consented_to_sms_marketing: false,
            };

            const response = await fetch('https://forms.shopifyapps.com/api/v2/form_submission', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(payload),
            });

            if (!response.ok) {
              throw new Error('submission_failed');
            }

            form.reset();
          } catch (error) {
            revertToForm();
            showToast('Something went wrong. Please try again.');
          } finally {
            if (widgetId !== null && window.hcaptcha) {
              window.hcaptcha.reset(widgetId);
            }
          }
        })();
      });
    }
  }

  function initFadeUp() {
    const elements = document.querySelectorAll('.fade-up');
    if (!elements.length || !('IntersectionObserver' in window)) {
      elements.forEach((el) => el.classList.add('visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    elements.forEach((el) => observer.observe(el));
  }

  function formatCount(value, decimals, suffix, displayPlus, target) {
    const atEnd = value >= target;
    let formatted;

    if (decimals > 0) {
      formatted = value.toFixed(decimals);
    } else {
      formatted = Math.floor(value).toLocaleString();
    }

    formatted += suffix || '';

    if (displayPlus && atEnd) {
      formatted += '+';
    }

    return formatted;
  }

  function animateCount(el, target, decimals, duration, suffix, displayPlus, onComplete) {
    const startTime = performance.now();

    function tick(now) {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const value = eased * target;
      el.textContent = formatCount(value, decimals, suffix, displayPlus, target);
      if (progress < 1) {
        requestAnimationFrame(tick);
      } else {
        el.textContent = formatCount(target, decimals, suffix, displayPlus, target);
        if (onComplete) onComplete();
      }
    }

    requestAnimationFrame(tick);
  }

  function initDelayedPrivacyBanner() {
    if (!document.body.classList.contains('hero-intro-pending')) return;

    const BANNER_DELAY_MS = 1000;
    let revealTimer = null;

    const reveal = () => {
      if (document.body.classList.contains('hero-intro-complete')) return;
      document.body.classList.remove('hero-intro-pending');
      document.body.classList.add('hero-intro-complete');
    };

    const scheduleReveal = (delayMs) => {
      if (revealTimer) clearTimeout(revealTimer);
      revealTimer = setTimeout(reveal, delayMs);
    };

    document.addEventListener('agape:hero-intro-complete', () => scheduleReveal(BANNER_DELAY_MS), {
      once: true,
    });
    setTimeout(reveal, 6000);
  }

  function initCountUps() {
    document.querySelectorAll('[data-count-up]:not([data-sync-hero-video])').forEach((el) => {
      const target = parseFloat(el.getAttribute('data-count-up') || '0');
      const decimals = parseInt(el.getAttribute('data-count-decimals') || '0', 10);
      const suffix = el.getAttribute('data-count-suffix') || '';
      const duration = parseInt(el.getAttribute('data-count-duration') || '2200', 10);
      const displayPlus = el.getAttribute('data-count-plus') === 'true';

      if (!('IntersectionObserver' in window)) {
        el.textContent = formatCount(target, decimals, suffix, displayPlus, target);
        return;
      }

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            animateCount(el, target, decimals, duration, suffix, displayPlus);
            observer.disconnect();
          }
        },
        { threshold: 0.1 }
      );

      observer.observe(el);
    });
  }

  function initStatCards() {
    document.querySelectorAll('[data-stat-card]').forEach((card) => {
      const valueEl = card.querySelector('[data-stat-value]');
      if (!valueEl) return;

      const target = parseFloat(card.getAttribute('data-stat-target') || '0');
      const decimals = parseInt(card.getAttribute('data-stat-decimals') || '0', 10);
      const suffix = card.getAttribute('data-stat-suffix') || '';
      const delay = parseInt(card.getAttribute('data-stat-delay') || '0', 10);

      if (!('IntersectionObserver' in window)) {
        card.classList.add('visible');
        valueEl.textContent = target.toFixed(decimals) + suffix;
        return;
      }

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              card.classList.add('visible');
              animateCount(valueEl, target, decimals, 1800, suffix, false);
            }, delay);
            observer.disconnect();
          }
        },
        { threshold: 0.2 }
      );

      observer.observe(card);
    });
  }

  function easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  function scrollToY(targetY, duration) {
    const startY = window.scrollY;
    const distance = targetY - startY;
    if (Math.abs(distance) < 1) return;

    const startTime = performance.now();

    function step(now) {
      const progress = Math.min((now - startTime) / duration, 1);
      window.scrollTo(0, startY + distance * easeOutCubic(progress));
      if (progress < 1) requestAnimationFrame(step);
    }

    requestAnimationFrame(step);
  }

  function getSamePageHashTarget(href) {
    if (!href || href === '#') return null;

    let url;
    try {
      url = new URL(href, window.location.href);
    } catch {
      return null;
    }

    if (url.origin !== window.location.origin) return null;
    if (url.pathname !== window.location.pathname) return null;
    if (!url.hash || url.hash === '#') return null;

    const el = document.querySelector(url.hash);
    if (!el) return null;

    return { el, hash: url.hash };
  }

  function initAnchorScroll() {
    const SCROLL_DURATION_MS = 450;

    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href*="#"]');
      if (!link) return;
      if (link.hasAttribute('download')) return;
      if (link.target === '_blank') return;

      const target = getSamePageHashTarget(link.getAttribute('href'));
      if (!target) return;

      event.preventDefault();

      const targetY = target.el.getBoundingClientRect().top + window.scrollY;
      scrollToY(targetY, SCROLL_DURATION_MS);

      if (history.pushState) {
        history.pushState(null, '', target.hash);
      } else {
        window.location.hash = target.hash;
      }
    });
  }

  function initHeroMobileLayout() {
    const heroes = document.querySelectorAll('[data-hero]');
    if (!heroes.length) return;

    const mobileQuery = window.matchMedia('(max-width: 767px)');

    function getViewportHeight() {
      if (window.visualViewport && window.visualViewport.height) {
        return window.visualViewport.height;
      }

      return window.innerHeight || document.documentElement.clientHeight || 0;
    }

    function updateHeroLayout() {
      const viewportHeight = getViewportHeight();

      heroes.forEach((hero) => {
        if (!mobileQuery.matches) {
          hero.style.removeProperty('--hero-mobile-video-top');
          hero.style.removeProperty('--hero-mobile-viewport-height');
          hero.style.removeProperty('--hero-mobile-section-min-height');
          return;
        }

        const canOffset = parseInt(hero.getAttribute('data-hero-mobile-can-offset') || '260', 10);
        const mediaWidth = parseFloat(hero.getAttribute('data-hero-media-width') || '2160');
        const mediaHeight = parseFloat(hero.getAttribute('data-hero-media-height') || '2576');
        const mediaAspectRatio = mediaWidth > 0 && mediaHeight > 0 ? mediaWidth / mediaHeight : 2160 / 2576;
        const renderedMediaHeight = hero.clientWidth > 0 && mediaAspectRatio > 0
          ? hero.clientWidth / mediaAspectRatio
          : 0;
        const scaledCanOffset = mediaHeight > 0 && renderedMediaHeight > 0
          ? (canOffset / mediaHeight) * renderedMediaHeight
          : canOffset;
        const videoTop = Math.max(viewportHeight - scaledCanOffset, 0);
        const sectionMinHeight = Math.max(viewportHeight, videoTop + renderedMediaHeight);

        hero.style.setProperty('--hero-mobile-viewport-height', `${viewportHeight}px`);
        hero.style.setProperty('--hero-mobile-video-top', `${videoTop}px`);
        hero.style.setProperty('--hero-mobile-section-min-height', `${sectionMinHeight}px`);
      });
    }

    updateHeroLayout();

    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', updateHeroLayout);
    } else if (typeof mobileQuery.addListener === 'function') {
      mobileQuery.addListener(updateHeroLayout);
    }

    window.addEventListener('orientationchange', updateHeroLayout, { passive: true });
    window.addEventListener('resize', updateHeroLayout, { passive: true });

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateHeroLayout, { passive: true });
    }
  }

  function initTickerTracks() {
    document.querySelectorAll('[data-ticker-track]').forEach((track) => {
      if (track.dataset.tickerCloned === 'true') return;
      const items = Array.from(track.children);
      if (!items.length) return;
      items.forEach((item) => track.appendChild(item.cloneNode(true)));
      track.dataset.tickerCloned = 'true';
    });
  }

  function runWhenIdle(callback) {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(callback, { timeout: 2000 });
      return;
    }
    setTimeout(callback, 1);
  }

  const navigationHintCache = new Set();

  function resolveSameOriginUrl(rawUrl) {
    if (!rawUrl) return null;

    let parsedUrl;
    try {
      parsedUrl = new URL(rawUrl, window.location.href);
    } catch {
      return null;
    }

    if (parsedUrl.origin !== window.location.origin) return null;
    if (!parsedUrl.pathname || parsedUrl.pathname === window.location.pathname) return null;

    return parsedUrl;
  }

  function appendHeadLinkOnce(rel, href, asValue) {
    const cacheKey = `${rel}:${href}`;
    if (navigationHintCache.has(cacheKey)) return;

    const existing = document.head.querySelector(`link[rel="${rel}"][href="${href}"]`);
    if (existing) {
      navigationHintCache.add(cacheKey);
      return;
    }

    const link = document.createElement('link');
    link.rel = rel;
    link.href = href;
    if (asValue) link.as = asValue;
    document.head.appendChild(link);
    navigationHintCache.add(cacheKey);
  }

  function primeSameOriginNavigation(rawUrl, options) {
    const settings = options || {};
    const resolvedUrl = resolveSameOriginUrl(rawUrl);
    if (!resolvedUrl) return;

    const href = resolvedUrl.href;

    if (settings.prerender !== false) {
      const prerenderKey = `prerender:${href}`;
      if (
        !navigationHintCache.has(prerenderKey) &&
        window.HTMLScriptElement &&
        typeof window.HTMLScriptElement.supports === 'function' &&
        window.HTMLScriptElement.supports('speculationrules')
      ) {
        const script = document.createElement('script');
        script.type = 'speculationrules';
        script.textContent = JSON.stringify({
          prerender: [
            {
              source: 'list',
              urls: [href]
            }
          ]
        });
        document.head.appendChild(script);
        navigationHintCache.add(prerenderKey);
      }
    }

    if (settings.prefetch !== false) {
      appendHeadLinkOnce('prefetch', href);
    }

    if (settings.warmDocument !== false && window.fetch) {
      const warmKey = `fetch:${href}`;
      if (!navigationHintCache.has(warmKey)) {
        navigationHintCache.add(warmKey);
        fetch(href, {
          credentials: 'same-origin',
          mode: 'same-origin'
        }).catch(() => {});
      }
    }
  }

  function initCollectionVarietyPreload() {
    if (!document.body.classList.contains('template-collection')) return;

    const varietyCta = document.querySelector('[data-variety-cta-link]');
    if (!varietyCta) return;

    const primeVarietyPage = () => {
      primeSameOriginNavigation(varietyCta.getAttribute('href'));
    };

    runWhenIdle(primeVarietyPage);

    ['pointerenter', 'focus', 'touchstart', 'mousedown'].forEach((eventName) => {
      varietyCta.addEventListener(eventName, primeVarietyPage, { passive: true, once: true });
    });
  }

  function initShopCardHoverGalleries() {
    const desktopQuery = window.matchMedia('(min-width: 768px)');
    const SLIDE_INTERVAL_MS = 1400;

    document.querySelectorAll('[data-shop-card-gallery]').forEach((gallery) => {
      const slides = Array.from(gallery.querySelectorAll('.shop-card__slide'));
      const dashes = Array.from(gallery.querySelectorAll('.shop-card__dash'));
      if (slides.length < 2) return;

      let activeIndex = 0;
      let timer = null;
      let progressFrame = null;
      let progressStart = 0;

      function setActive(index) {
        activeIndex = index;
        slides.forEach((slide, i) => {
          slide.classList.toggle('is-active', i === index);
        });
        dashes.forEach((dash, i) => {
          dash.classList.toggle('is-active', i === index);
          dash.classList.toggle('is-complete', i < index);
          if (i < index) {
            dash.style.setProperty('--dash-progress', '100%');
          } else if (i === index) {
            dash.style.setProperty('--dash-progress', '0%');
          } else {
            dash.style.removeProperty('--dash-progress');
          }
        });
      }

      function stop() {
        gallery.classList.remove('is-hovering');
        if (timer) {
          clearInterval(timer);
          timer = null;
        }
        if (progressFrame) {
          cancelAnimationFrame(progressFrame);
          progressFrame = null;
        }
        slides.forEach((slide) => slide.classList.remove('is-active'));
        dashes.forEach((dash) => {
          dash.classList.remove('is-active', 'is-complete');
          dash.style.removeProperty('--dash-progress');
        });
        activeIndex = 0;
      }

      function animateProgress() {
        const activeDash = dashes[activeIndex];
        if (!activeDash || !gallery.classList.contains('is-hovering')) return;

        const elapsed = performance.now() - progressStart;
        const progress = Math.min(elapsed / SLIDE_INTERVAL_MS, 1);
        activeDash.style.setProperty('--dash-progress', `${progress * 100}%`);

        if (progress < 1) {
          progressFrame = requestAnimationFrame(animateProgress);
        }
      }

      function startProgress() {
        progressStart = performance.now();
        if (progressFrame) cancelAnimationFrame(progressFrame);
        progressFrame = requestAnimationFrame(animateProgress);
      }

      function next() {
        if (slides.length < 2) return;
        setActive((activeIndex + 1) % slides.length);
        startProgress();
      }

      gallery.addEventListener('mouseenter', () => {
        if (!desktopQuery.matches) return;

        gallery.classList.add('is-hovering');
        setActive(0);
        startProgress();
        if (slides.length > 1) {
          timer = setInterval(next, SLIDE_INTERVAL_MS);
        }
      });

      gallery.addEventListener('mouseleave', stop);

      if (typeof desktopQuery.addEventListener === 'function') {
        desktopQuery.addEventListener('change', (event) => {
          if (!event.matches) stop();
        });
      } else if (typeof desktopQuery.addListener === 'function') {
        desktopQuery.addListener((event) => {
          if (!event.matches) stop();
        });
      }
    });
  }

  const DISCOUNT_STORAGE_KEY = 'agape-active-discount-code';
  const IID_STORAGE_KEY = 'agape-attribution-iid';

  function normalizeDiscountCode(value) {
    return String(value || '').trim().toLowerCase();
  }

  function getShopifyCartRoutes() {
    return (window.Shopify && window.Shopify.routes) || {
      cart_url: '/cart',
      cart_update_url: '/cart/update.js',
    };
  }

  function readCookieDiscountCode() {
    const parts = document.cookie.split(';');
    for (const part of parts) {
      const trimmed = part.trim();
      if (!trimmed) continue;
      const eqIndex = trimmed.indexOf('=');
      const name = eqIndex === -1 ? trimmed : trimmed.slice(0, eqIndex);
      if (name !== 'discount_code') continue;
      const value = eqIndex === -1 ? '' : trimmed.slice(eqIndex + 1);
      try {
        return decodeURIComponent(value).trim();
      } catch (error) {
        return value.trim();
      }
    }
    return '';
  }

  function parseDiscountFromPath() {
    const match = window.location.pathname.match(/^\/discount\/([^/?#]+)/i);
    if (!match) return '';
    try {
      return decodeURIComponent(match[1]).trim();
    } catch (error) {
      return match[1].trim();
    }
  }

  function readQueryParam(name) {
    try {
      return (new URLSearchParams(window.location.search).get(name) || '').trim();
    } catch (error) {
      return '';
    }
  }

  // Shopify's /discount/{code} hop always 302s to the storefront, so an ambassador link
  // is only ever observable here as ?discount= on the landing URL.
  function parseDiscountFromQuery() {
    return readQueryParam('discount');
  }

  function parseIidFromQuery() {
    return readQueryParam('iid');
  }

  function persistIid(iid) {
    if (!iid) return;
    try {
      sessionStorage.setItem(IID_STORAGE_KEY, iid);
      localStorage.setItem(IID_STORAGE_KEY, iid);
    } catch (error) {
      /* ignore storage errors */
    }
  }

  function getStoredIid() {
    try {
      return sessionStorage.getItem(IID_STORAGE_KEY) || localStorage.getItem(IID_STORAGE_KEY) || '';
    } catch (error) {
      return '';
    }
  }

  // Set when a card is tapped on this page load without carrying its own code, meaning
  // the iid — not whatever is in storage — decides attribution.
  let pendingIidResolution = '';

  // Turns a card's iid into the discount code it should apply. Shopify drops query
  // params at checkout, so a link carrying only an iid cannot attribute itself; the
  // affiliate API owns the iid -> code mapping and hands it back here.
  async function fetchDiscountCodeForIid(iid) {
    const config = window.AgapeAffiliate || {};
    const base = String(config.apiBaseUrl || '').replace(/\/+$/, '');
    if (!base || !iid || !window.fetch) return '';

    try {
      const response = await fetch(`${base}/iid/${encodeURIComponent(iid)}/discount`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return '';
      const data = await response.json();
      return data && typeof data.code === 'string' ? data.code.trim() : '';
    } catch (error) {
      return '';
    }
  }

  function persistDiscountCode(code) {
    if (!code) return;
    try {
      sessionStorage.setItem(DISCOUNT_STORAGE_KEY, code);
      localStorage.setItem(DISCOUNT_STORAGE_KEY, code);
    } catch (error) {
      /* ignore storage errors */
    }
  }

  function getStoredDiscountCode() {
    try {
      return sessionStorage.getItem(DISCOUNT_STORAGE_KEY) || localStorage.getItem(DISCOUNT_STORAGE_KEY) || '';
    } catch (error) {
      return '';
    }
  }

  let activeDiscountState = { code: '', percent: null, valueType: null, resolved: false, applicable: true };

  function getActiveDiscountCode() {
    return activeDiscountState.code || '';
  }

  function getActiveDiscountPercent() {
    return activeDiscountState.percent;
  }

  function updateDiscountDomAttributes() {
    const code = activeDiscountState.code;
    const percent = activeDiscountState.percent;

    if (!code) {
      document.documentElement.classList.remove('agape-discount-active');
      document.documentElement.removeAttribute('data-agape-discount-code');
      document.documentElement.removeAttribute('data-agape-discount-percent');
      return;
    }

    document.documentElement.classList.add('agape-discount-active');
    document.documentElement.setAttribute('data-agape-discount-code', code);
    if (percent) {
      document.documentElement.setAttribute('data-agape-discount-percent', String(percent));
    } else {
      document.documentElement.removeAttribute('data-agape-discount-percent');
    }
  }

  function normalizeDiscountPercent(percent) {
    const value = Number(percent);
    if (!Number.isFinite(value) || value <= 0) return null;
    return Math.round(value);
  }

  function parseDiscountFromCartResponse(cart, code) {
    const target = normalizeDiscountCode(code);
    if (!cart || !target) {
      return { percent: null, resolved: false, applicable: false };
    }

    const discountCodes = Array.isArray(cart.discount_codes) ? cart.discount_codes : [];
    const matchingCode = discountCodes.find((entry) => normalizeDiscountCode(entry.code) === target);
    if (matchingCode && matchingCode.applicable === false) {
      return { percent: null, resolved: true, applicable: false };
    }

    const applications = Array.isArray(cart.cart_level_discount_applications)
      ? cart.cart_level_discount_applications
      : [];

    for (const application of applications) {
      const title = normalizeDiscountCode(application.title || application.code);
      if (!title || (title !== target && !title.includes(target) && !target.includes(title))) {
        continue;
      }

      if (application.value_type === 'percentage') {
        const percent = parseFloat(application.value);
        if (Number.isFinite(percent) && percent > 0) {
          return {
            percent: normalizeDiscountPercent(percent),
            valueType: 'percentage',
            resolved: true,
            applicable: true,
          };
        }
      }

      return {
        percent: null,
        valueType: application.value_type || null,
        resolved: true,
        applicable: true,
      };
    }

    if (matchingCode && matchingCode.applicable) {
      const originalSubtotal = Number(cart.items_subtotal_price || cart.original_total_price || 0);
      const totalDiscount = Number(cart.total_discount || 0);
      if (originalSubtotal > 0 && totalDiscount > 0) {
        const derivedPercent = (totalDiscount / originalSubtotal) * 100;
        if (Number.isFinite(derivedPercent) && derivedPercent > 0 && derivedPercent < 100) {
          return {
            percent: normalizeDiscountPercent(derivedPercent),
            valueType: 'derived',
            resolved: true,
            applicable: true,
          };
        }
      }

      if (applications.length === 1) {
        const application = applications[0];
        if (application.value_type === 'percentage') {
          const percent = parseFloat(application.value);
          if (Number.isFinite(percent) && percent > 0) {
            return {
              percent: normalizeDiscountPercent(percent),
              valueType: 'percentage',
              resolved: true,
              applicable: true,
            };
          }
        }
      }

      return { percent: null, resolved: true, applicable: true };
    }

    return { percent: null, resolved: false, applicable: true };
  }

  async function fetchCartState() {
    const routes = getShopifyCartRoutes();
    const cartUrl = routes.cart_url || '/cart';
    const response = await fetch(`${cartUrl}.js`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return null;
    return response.json();
  }

  // Pushes both attribution signals onto the cart in one request. The discount code is
  // what Shopify actually applies (and what the backend attributes commission from);
  // the iid rides along as a cart attribute, which is one of the few values that
  // survives checkout onto the resulting order, so the specific card that drove a sale
  // stays recoverable server-side.
  async function syncCartAttribution(code) {
    const routes = getShopifyCartRoutes();
    const updateUrl = routes.cart_update_url || '/cart/update.js';
    if (!updateUrl || !window.fetch) return null;

    const iid = getStoredIid();
    if (!code && !iid) return null;

    const payload = {};
    if (code) payload.discount = code;
    if (iid) payload.attributes = { iid };

    const response = await fetch(updateUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) return null;
    return response.json();
  }

  async function resolveActiveDiscountFromCart() {
    let code = activeDiscountState.code || getStoredDiscountCode();

    // Tracks the iid already looked up on this page load so an unknown one is not
    // requested twice by the two branches below.
    let attemptedIid = '';

    // A card tapped on this page load decides attribution, even when an older partner's
    // code is still sitting in storage.
    if (pendingIidResolution) {
      attemptedIid = pendingIidResolution;
      pendingIidResolution = '';
      const resolved = await fetchDiscountCodeForIid(attemptedIid);
      if (resolved) {
        persistDiscountCode(resolved);
        code = resolved;
      }
    }

    // An iid-only link carries no code of its own, so go ask for it.
    if (!code) {
      const storedIid = getStoredIid();
      if (storedIid && storedIid !== attemptedIid) {
        const resolved = await fetchDiscountCodeForIid(storedIid);
        if (resolved) {
          persistDiscountCode(resolved);
          code = resolved;
        }
      }
    }

    if (!code) {
      // No code, but a card tap may still have left an iid worth recording on the cart.
      try {
        await syncCartAttribution('');
      } catch (error) {
        /* ignore attribution sync errors */
      }
      activeDiscountState = { code: '', percent: null, valueType: null, resolved: false, applicable: true };
      updateDiscountDomAttributes();
      return activeDiscountState;
    }

    let cart = null;
    try {
      cart = await syncCartAttribution(code);
      if (!cart) {
        cart = await fetchCartState();
      }
    } catch (error) {
      cart = null;
    }

    const parsed = parseDiscountFromCartResponse(cart, code);
    activeDiscountState = {
      code,
      percent: normalizeDiscountPercent(parsed.percent),
      valueType: parsed.valueType || null,
      resolved: Boolean(parsed.resolved),
      applicable: parsed.applicable !== false,
    };
    updateDiscountDomAttributes();
    return activeDiscountState;
  }

  function applyDiscountDisplayCents(cents) {
    const percent = getActiveDiscountPercent();
    const original = Number(cents);
    if (!percent || !Number.isFinite(original)) return original;
    return Math.round(original * (1 - percent / 100));
  }

  function formatMoneyCents(cents) {
    const dollars = Number(cents) / 100;
    if (!Number.isFinite(dollars)) return '';
    if (Number.isInteger(dollars)) {
      return `$${dollars}`;
    }
    return `$${dollars.toFixed(2)}`;
  }

  function initActiveDiscount() {
    const iidFromQuery = parseIidFromQuery();
    persistIid(iidFromQuery);

    // A freshly clicked ambassador link has to beat a code left over from an earlier
    // visit, otherwise whichever partner a shopper happened to click first would keep
    // the credit forever.
    const fromLink = parseDiscountFromQuery() || parseDiscountFromPath();
    if (fromLink) {
      persistDiscountCode(fromLink);
    } else if (iidFromQuery) {
      // Tapped card with no code in the URL — resolve it from the iid rather than
      // letting a stale stored code keep the credit.
      pendingIidResolution = iidFromQuery;
    }

    let code = fromLink || getStoredDiscountCode();
    if (!code) {
      code = readCookieDiscountCode();
      if (code) persistDiscountCode(code);
    }

    activeDiscountState = {
      code: code || '',
      percent: null,
      valueType: null,
      resolved: false,
      applicable: true,
    };

    if (code) {
      updateDiscountDomAttributes();
    }

    return activeDiscountState;
  }

  function notifyActiveDiscount() {
    if (!activeDiscountState.code) return;
    document.dispatchEvent(
      new CustomEvent('agape:discount-code-active', {
        detail: { ...activeDiscountState },
      })
    );
  }

  function ensurePriceCompareElement(priceEl) {
    if (!priceEl) return null;
    let compareEl = priceEl.querySelector('[data-agape-price-compare]');
    if (!compareEl) {
      compareEl = document.createElement('span');
      compareEl.setAttribute('data-agape-price-compare', '');
      compareEl.className = 'agape-price-compare';
      compareEl.hidden = true;
      priceEl.insertBefore(compareEl, priceEl.firstChild);
    } else if (compareEl.parentElement === priceEl && priceEl.firstElementChild !== compareEl) {
      priceEl.insertBefore(compareEl, priceEl.firstChild);
    }
    return compareEl;
  }

  function ensurePriceDisplayElement(priceEl) {
    if (!priceEl) return null;
    let displayEl = priceEl.querySelector('[data-agape-price-display]');
    if (!displayEl) {
      const existingText = Array.from(priceEl.childNodes)
        .filter((node) => node.nodeType === Node.TEXT_NODE)
        .map((node) => node.textContent || '')
        .join('')
        .trim();
      displayEl = document.createElement('span');
      displayEl.setAttribute('data-agape-price-display', '');
      if (existingText) {
        displayEl.textContent = existingText;
      }
      priceEl.appendChild(displayEl);
      Array.from(priceEl.childNodes).forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          priceEl.removeChild(node);
        }
      });
    }
    ensurePriceCompareElement(priceEl);
    return displayEl;
  }

  function setPriceDisplayText(priceEl, text) {
    const displayEl = ensurePriceDisplayElement(priceEl);
    if (displayEl) {
      displayEl.textContent = text;
    }
  }

  function getDiscountPriceElement(container) {
    if (!container) return null;
    if (container.hasAttribute('data-agape-price-cents')) {
      return container;
    }
    return container.querySelector('[data-agape-price-cents]');
  }

  function refreshDiscountPriceElements(root) {
    const scope = root && root.querySelectorAll ? root : document;
    const code = getActiveDiscountCode();
    const percent = getActiveDiscountPercent();
    const canShowMath = Boolean(code && percent);

    scope.querySelectorAll('[data-agape-price-cents]').forEach((priceEl) => {
      const originalCents = parseInt(priceEl.getAttribute('data-agape-price-cents') || '', 10);
      if (!Number.isFinite(originalCents)) return;

      const serverFinalCents = parseInt(priceEl.getAttribute('data-agape-price-final') || '', 10);
      const formattedOriginal = formatMoneyCents(originalCents);
      const compareEl = ensurePriceCompareElement(priceEl);

      if (Number.isFinite(serverFinalCents) && serverFinalCents < originalCents) {
        priceEl.setAttribute('data-agape-original-text', formattedOriginal);
        setPriceDisplayText(priceEl, formatMoneyCents(serverFinalCents));
        if (compareEl) {
          compareEl.textContent = formattedOriginal;
          compareEl.hidden = false;
        }
        return;
      }

      if (!canShowMath) {
        setPriceDisplayText(priceEl, formattedOriginal);
        priceEl.removeAttribute('data-agape-original-text');
        if (compareEl) compareEl.hidden = true;
        return;
      }

      priceEl.setAttribute('data-agape-original-text', formattedOriginal);
      const discountedCents = applyDiscountDisplayCents(originalCents);
      setPriceDisplayText(priceEl, formatMoneyCents(discountedCents));

      if (compareEl) {
        compareEl.textContent = formattedOriginal;
        compareEl.hidden = discountedCents >= originalCents;
      }
    });

    scope.querySelectorAll('.merch-card__compare:not([data-agape-price-compare])').forEach((el) => {
      el.hidden = canShowMath;
    });

    scope.querySelectorAll('.agape-product__pack-compare').forEach((el) => {
      el.hidden = canShowMath;
    });
  }

  function updateDiscountNotes(root) {
    const scope = root && root.querySelectorAll ? root : document;
    const code = getActiveDiscountCode();
    const percent = getActiveDiscountPercent();

    scope.querySelectorAll('[data-agape-discount-note]').forEach((note) => {
      if (!code) {
        note.hidden = true;
        return;
      }

      const codeEl = note.querySelector('[data-agape-discount-note-code]');
      if (codeEl) codeEl.textContent = code;

      const detailEl = note.querySelector('[data-agape-discount-note-detail]');
      if (detailEl) {
        if (!activeDiscountState.applicable) {
          detailEl.textContent = 'This code could not be applied. Check the code or try again at checkout.';
        } else if (percent) {
          detailEl.textContent = `${percent}% off — final total at checkout.`;
        } else {
          detailEl.textContent = 'Your code is applied — savings show at checkout.';
        }
      }

      note.hidden = false;
    });
  }

  function syncCheckoutDiscountInputs() {
    const code = getActiveDiscountCode();
    document.querySelectorAll('[data-checkout-discount-input]').forEach((input) => {
      if (code) {
        input.value = code;
        input.removeAttribute('disabled');
      } else {
        input.value = '';
        input.setAttribute('disabled', 'disabled');
      }
    });
  }

  async function syncCartDiscountCode() {
    const code = getActiveDiscountCode();
    if (!code) return null;
    try {
      return await syncCartAttribution(code);
    } catch (error) {
      return null;
    }
  }

  function refreshDiscountPresentation(root) {
    refreshDiscountPriceElements(root);
    updateDiscountNotes(root);
    syncCheckoutDiscountInputs();
  }

  function initDiscountPresentation() {
    refreshDiscountPresentation(document);

    document.addEventListener('agape:discount-code-active', () => {
      refreshDiscountPresentation(document);
    });
  }

  function applyAnnouncementDiscountState() {
    const code = getActiveDiscountCode();
    const percent = getActiveDiscountPercent();
    if (!code) return;

    const bar = document.querySelector('[data-announcement-bar]');
    const modal = document.querySelector('[data-announcement-modal]');
    if (!bar && !modal) return;

    document.querySelectorAll('[data-announcement-default-copy]').forEach((el) => {
      el.hidden = true;
    });
    document.querySelectorAll('[data-announcement-applied-copy]').forEach((el) => {
      el.hidden = false;
    });

    document.querySelectorAll('[data-announcement-applied-code]').forEach((el) => {
      el.textContent = code;
    });

    document.querySelectorAll('[data-announcement-applied-percent]').forEach((el) => {
      if (percent) {
        el.textContent = String(percent);
        el.hidden = false;
      } else {
        el.hidden = true;
      }
    });

    document.querySelectorAll('[data-announcement-applied-percent-wrap]').forEach((el) => {
      el.hidden = !percent;
    });

    document.querySelectorAll('[data-announcement-form-content]').forEach((el) => {
      el.hidden = true;
    });
    document.querySelectorAll('[data-announcement-success]').forEach((el) => {
      el.hidden = true;
    });
    document.querySelectorAll('[data-announcement-applied-content]').forEach((el) => {
      el.hidden = false;
    });

    if (bar) {
      bar.setAttribute('aria-label', `Discount code ${code} applied`);
    }
  }

  function getCartRoutes() {
    return (window.Shopify && window.Shopify.routes) || {
      root: '/',
      cart_add_url: '/cart/add.js',
      cart_change_url: '/cart/change.js',
      cart_update_url: '/cart/update.js',
      cart_url: '/cart',
    };
  }

  async function cartApiFetch(url, options) {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(options && options.headers ? options.headers : {}),
      },
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.description || payload.message || 'Could not update your cart.');
    }
    return payload;
  }

  function updateCartCountDisplay(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = String(count);
      el.hidden = count <= 0;
    });
    document.querySelectorAll('[data-cart-drawer-count]').forEach((el) => {
      el.textContent = `(${count})`;
    });
  }

  function getDisplayedCartCount() {
    const badge = document.querySelector('[data-cart-count]');
    if (badge) {
      return parseInt(badge.textContent || '0', 10) || 0;
    }

    const drawerCount = document.querySelector('[data-cart-drawer-count]');
    if (drawerCount) {
      const raw = (drawerCount.textContent || '').replace(/[^\d-]/g, '');
      return parseInt(raw || '0', 10) || 0;
    }

    const drawerRoot = getDrawerCartRoot();
    if (!drawerRoot) return 0;

    let count = 0;
    drawerRoot.querySelectorAll('[data-cart-line]').forEach((line) => {
      if (line.hidden) return;
      const qtyInput = line.querySelector('[data-qty-input]');
      count += parseInt(qtyInput ? qtyInput.value || '0' : '0', 10);
    });
    return count;
  }

  function getDrawerCartRoot() {
    return document.querySelector('[data-agape-cart][data-cart-drawer-section]');
  }

  function getPageCartRoot() {
    return document.querySelector('.agape-cart-section [data-agape-cart], .template-cart [data-agape-cart]:not([data-cart-drawer-section])');
  }

  function getRefreshableCartRoot() {
    if (document.body.classList.contains('template-cart')) {
      return getPageCartRoot();
    }
    return getDrawerCartRoot() || getPageCartRoot();
  }

  function parseCartSectionMarkup(html) {
    const template = document.createElement('template');
    template.innerHTML = html.trim();
    return template.content.querySelector('[data-agape-cart]');
  }

  function buildSectionRenderUrl(path, sectionId) {
    const url = new URL(path, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    return url.toString();
  }

  function getCartSectionRenderPaths() {
    const routes = getCartRoutes();
    const paths = document.body.classList.contains('template-cart')
      ? [routes.cart_url]
      : [
          `${window.location.pathname}${window.location.search}`,
          routes.root || '/',
          routes.cart_url,
        ];

    return paths.filter((path, index) => path && paths.indexOf(path) === index);
  }

  let cartDrawerLastFocus = null;
  let cartNoteSaveTimer = null;
  let cartDrawerPreloadStarted = false;
  const lineQuantitySync = new Map();
  const LINE_QUANTITY_SYNC_MS = 180;

  function getLineUnitCents(line) {
    if (line.dataset.unitCents) {
      return parseInt(line.dataset.unitCents, 10) || 0;
    }
    const lineTotalEl = line.querySelector('[data-line-total]');
    const qtyInput = line.querySelector('[data-qty-input]');
    if (!lineTotalEl || !qtyInput) return 0;
    const qty = Math.max(1, parseInt(qtyInput.value || '1', 10));
    const lineCents = parseInt(lineTotalEl.getAttribute('data-agape-price-cents') || '0', 10);
    return qty > 0 ? Math.round(lineCents / qty) : lineCents;
  }

  function cacheLineUnitCents(line) {
    if (!line.dataset.unitCents) {
      const unitCents = getLineUnitCents(line);
      if (unitCents > 0) line.dataset.unitCents = String(unitCents);
    }
  }

  function setLineQuantityDisplay(line, quantity) {
    const qtyInput = line.querySelector('[data-qty-input]');
    const lineTotalEl = line.querySelector('[data-line-total]');
    if (qtyInput) qtyInput.value = String(Math.max(0, quantity));

    if (!lineTotalEl || quantity <= 0) return;

    cacheLineUnitCents(line);
    const unitCents = parseInt(line.dataset.unitCents || '0', 10);
    const newLineCents = unitCents * quantity;
    lineTotalEl.setAttribute('data-agape-price-cents', String(newLineCents));
    lineTotalEl.removeAttribute('data-agape-price-final');
  }

  function recomputeDrawerSubtotal(cartRoot) {
    let totalCents = 0;
    cartRoot.querySelectorAll('[data-cart-line]').forEach((line) => {
      if (line.hidden) return;
      const lineTotalEl = line.querySelector('[data-line-total]');
      if (lineTotalEl) {
        totalCents += parseInt(lineTotalEl.getAttribute('data-agape-price-cents') || '0', 10);
      }
    });

    cartRoot.querySelectorAll('[data-cart-subtotal]').forEach((el) => {
      el.setAttribute('data-agape-price-cents', String(totalCents));
      el.removeAttribute('data-agape-price-final');
    });
  }

  function updateDrawerCartCountFromLines(cartRoot) {
    let count = 0;
    cartRoot.querySelectorAll('[data-cart-line]').forEach((line) => {
      if (line.hidden) return;
      const qtyInput = line.querySelector('[data-qty-input]');
      count += parseInt(qtyInput ? qtyInput.value || '0' : '0', 10);
    });
    updateCartCountDisplay(count);
  }

  function applyCartTotalsFromJson(cart, root) {
    let originalSubtotal = 0;
    let finalSubtotal = 0;
    if (Array.isArray(cart.items)) {
      cart.items.forEach((item) => {
        originalSubtotal += item.original_line_price || 0;
        finalSubtotal += item.final_line_price || item.original_line_price || 0;
      });
    }

    root.querySelectorAll('[data-cart-subtotal]').forEach((el) => {
      el.setAttribute('data-agape-price-cents', String(originalSubtotal));
      if (finalSubtotal < originalSubtotal) {
        el.setAttribute('data-agape-price-final', String(finalSubtotal));
      } else {
        el.removeAttribute('data-agape-price-final');
      }
    });
  }

  function reconcileLineFromCart(line, cart, lineKey) {
    if (!Array.isArray(cart.items)) return;
    const item = cart.items.find((entry) => entry.key === lineKey);
    if (!item) return;

    line.hidden = false;
    setLineQuantityDisplay(line, item.quantity);
    const lineTotalEl = line.querySelector('[data-line-total]');
    if (lineTotalEl) {
      lineTotalEl.setAttribute('data-agape-price-cents', String(item.original_line_price));
      if (item.final_line_price < item.original_line_price) {
        lineTotalEl.setAttribute('data-agape-price-final', String(item.final_line_price));
      } else {
        lineTotalEl.removeAttribute('data-agape-price-final');
      }
    }
    if (item.quantity > 0) {
      line.dataset.unitCents = String(Math.round(item.original_line_price / item.quantity));
    }
  }

  function getLineQuantity(value, fallback) {
    const quantity = parseInt(value, 10);
    return Number.isFinite(quantity) ? Math.max(0, quantity) : fallback;
  }

  function applyOptimisticLineQuantity(line, quantity, cartRoot) {
    cacheLineUnitCents(line);
    if (quantity <= 0) {
      line.hidden = true;
    } else {
      line.hidden = false;
      setLineQuantityDisplay(line, quantity);
    }

    updateDrawerCartCountFromLines(cartRoot);
    recomputeDrawerSubtotal(cartRoot);
    refreshDiscountPresentation(cartRoot);
  }

  function scheduleLineQuantity(line, lineKey, lineNumber, quantity, cartRoot, delay) {
    const state = lineQuantitySync.get(lineKey) || { seq: 0 };
    state.targetQty = Math.max(0, quantity);
    state.seq += 1;
    state.line = line;
    state.lineNumber = lineNumber;
    state.root = cartRoot;
    lineQuantitySync.set(lineKey, state);
    applyOptimisticLineQuantity(line, state.targetQty, cartRoot);

    if (state.inFlight && state.controller) {
      state.controller.abort();
      state.inFlight = false;
    }

    clearTimeout(state.timer);
    state.timer = window.setTimeout(() => {
      flushLineQuantity(lineKey);
    }, delay === undefined ? LINE_QUANTITY_SYNC_MS : delay);
  }

  async function flushLineQuantity(lineKey) {
    const state = lineQuantitySync.get(lineKey);
    if (!state || state.inFlight) return;

    const requestSeq = state.seq;
    const { targetQty, line, root } = state;
    state.inFlight = true;
    const controller = new AbortController();
    state.controller = controller;

    try {
      const cart = await changeCartLineQuantity(lineKey, targetQty, state.lineNumber, controller.signal);
      const latest = lineQuantitySync.get(lineKey);
      if (!latest) return;

      const isCurrentRequest = latest.controller === controller;
      if (isCurrentRequest) {
        latest.inFlight = false;
        latest.controller = null;
      }
      if (latest.seq !== requestSeq) {
        if (!latest.inFlight) {
          scheduleLineQuantity(
            latest.line,
            lineKey,
            latest.lineNumber,
            latest.targetQty,
            latest.root,
            0
          );
        }
        return;
      }

      if (targetQty === 0) {
        line.remove();
        lineQuantitySync.delete(lineKey);
        if (cart.item_count === 0) {
          await refreshCartSection({ preserveDrawer: true });
          return;
        }
      } else {
        reconcileLineFromCart(line, cart, lineKey);
        lineQuantitySync.delete(lineKey);
      }

      applyCartTotalsFromJson(cart, root);
      updateDrawerCartCountFromLines(root);
      refreshDiscountPresentation(root);
    } catch (error) {
      const latest = lineQuantitySync.get(lineKey);
      const wasSuperseded = latest && latest.seq !== requestSeq;
      const isCurrentRequest = latest && latest.controller === controller;
      if (isCurrentRequest) {
        latest.inFlight = false;
        latest.controller = null;
      }

      if (error.name === 'AbortError' && wasSuperseded) {
        if (!latest.inFlight) {
          scheduleLineQuantity(
            latest.line,
            lineKey,
            latest.lineNumber,
            latest.targetQty,
            latest.root,
            0
          );
        }
        return;
      }

      if (latest && isCurrentRequest) {
        lineQuantitySync.delete(lineKey);
      }
      handleCartInteractionError(error);
      await refreshCartSection({ preserveDrawer: true });
    }
  }

  function preserveDrawerOpenOnSection(sectionEl, open) {
    if (!open || !sectionEl) return;
    const drawer = sectionEl.querySelector('[data-cart-drawer]');
    if (!drawer) return;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-drawer-open');
    document.body.style.overflow = 'hidden';
  }

  function setCartDrawerOpen(open) {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;

    if (open) {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('cart-drawer-open');
      cartDrawerLastFocus = document.activeElement;
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(() => {
        const closeBtn = drawer.querySelector('.cart-drawer__close');
        if (closeBtn) closeBtn.focus();
      });
      return;
    }

    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-drawer-open');
    document.body.style.overflow = '';
    if (cartDrawerLastFocus && typeof cartDrawerLastFocus.focus === 'function') {
      cartDrawerLastFocus.focus();
    }
    cartDrawerLastFocus = null;
  }

  function openCartDrawer() {
    setCartDrawerOpen(true);
  }

  function closeCartDrawer() {
    setCartDrawerOpen(false);
  }

  function setCartBusyState(busy) {
    document.querySelectorAll('[data-cart-checkout], [data-cart-remove], [data-qty-minus], [data-qty-plus], [data-qty-input]').forEach((el) => {
      el.disabled = busy;
      if (el.hasAttribute('data-cart-checkout')) {
        el.setAttribute('aria-busy', busy ? 'true' : 'false');
      }
    });
  }

  function setCheckoutBusyState(root, busy) {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll('[data-cart-checkout]').forEach((button) => {
      button.disabled = busy;
      button.setAttribute('aria-busy', busy ? 'true' : 'false');
    });
  }

  function setCartDrawerLoading(loading) {
    const drawerBody = document.querySelector('[data-cart-drawer-body]');
    if (drawerBody) drawerBody.classList.toggle('is-loading', loading);
  }

  function preloadCartDrawerSection() {
    if (cartDrawerPreloadStarted) return;
    const root = getDrawerCartRoot();
    if (!root) return;

    const sectionId = root.getAttribute('data-section-id');
    if (!sectionId) return;

    cartDrawerPreloadStarted = true;
    const routes = getCartRoutes();
    const basePath = routes.root || '/';
    fetch(`${basePath}?section_id=${sectionId}`, { credentials: 'same-origin' }).catch(() => {});
    fetch(`${routes.cart_url}.js`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    }).catch(() => {});
  }

  function showDrawerCartError(message) {
    const errorEl = document.querySelector('[data-cart-drawer-error]');
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = !message;
  }

  async function fetchCartJson() {
    const routes = getCartRoutes();
    return cartApiFetch(`${routes.cart_url}.js`, { method: 'GET' });
  }

  async function changeCartLineQuantity(lineKey, quantity, lineNumber, signal) {
    const routes = getCartRoutes();
    const request = (body) => cartApiFetch(routes.cart_change_url, {
      method: 'POST',
      body: JSON.stringify(body),
      signal,
    });

    try {
      return await request({ id: lineKey, quantity });
    } catch (error) {
      const line = parseInt(lineNumber, 10);
      if (!Number.isFinite(line) || line < 1) throw error;
      return request({ line, quantity });
    }
  }

  async function updateCartNote(note) {
    const routes = getCartRoutes();
    return cartApiFetch(routes.cart_update_url, {
      method: 'POST',
      body: JSON.stringify({ note }),
    });
  }

  async function postAddToCart(variantId, quantity, sellingPlanId) {
    const routes = getCartRoutes();
    const payload = { id: variantId, quantity };
    if (sellingPlanId) payload.selling_plan = sellingPlanId;
    return cartApiFetch(routes.cart_add_url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  function bindCartNoteControls(root) {
    const noteWrap = root.querySelector('[data-cart-order-note]');
    if (!noteWrap || noteWrap.dataset.bound === 'true') return;

    const toggle = noteWrap.querySelector('[data-cart-note-toggle]');
    const panel = noteWrap.querySelector('[data-cart-note-panel]');
    const input = noteWrap.querySelector('[data-cart-note-input]');
    const status = noteWrap.querySelector('[data-cart-note-status]');
    let noteSaveRequestsInFlight = 0;

    if (toggle && panel) {
      toggle.addEventListener('click', () => {
        const expanded = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        panel.hidden = expanded;
        if (!expanded && input) input.focus();
      });
    }

    if (input) {
      const saveNote = () => {
        if (cartNoteSaveTimer) clearTimeout(cartNoteSaveTimer);
        cartNoteSaveTimer = setTimeout(async () => {
          if (status) {
            status.hidden = true;
            status.textContent = '';
          }
          noteSaveRequestsInFlight += 1;
          setCheckoutBusyState(root, true);
          try {
            await updateCartNote(input.value);
          } catch (error) {
            if (status) {
              status.textContent = 'Could not save note. Try again.';
              status.hidden = false;
            }
          } finally {
            noteSaveRequestsInFlight = Math.max(0, noteSaveRequestsInFlight - 1);
            if (!noteSaveRequestsInFlight) {
              setCheckoutBusyState(root, false);
            }
          }
        }, 400);
      };

      input.addEventListener('input', saveNote);
      input.addEventListener('blur', saveNote);
    }

    noteWrap.dataset.bound = 'true';
  }

  function bindCartLineControls(root) {
    root.querySelectorAll('[data-cart-line]').forEach((line) => {
      if (line.dataset.lineControlsBound === 'true') return;

      const qtyControl = line.querySelector('[data-qty-control]');
      const qtyInput = line.querySelector('[data-qty-input]');
      const minus = qtyControl ? qtyControl.querySelector('[data-qty-minus]') : null;
      const plus = qtyControl ? qtyControl.querySelector('[data-qty-plus]') : null;
      const lineKey = line.getAttribute('data-line-key');
      const lineNumber = line.getAttribute('data-line');
      if (!qtyInput || !lineKey) return;

      if (minus) {
        minus.addEventListener('click', () => {
          const current = getLineQuantity(qtyInput.value, 1);
          scheduleLineQuantity(line, lineKey, lineNumber, current - 1, root);
        });
      }

      if (plus) {
        plus.addEventListener('click', () => {
          const current = getLineQuantity(qtyInput.value, 0);
          scheduleLineQuantity(line, lineKey, lineNumber, current + 1, root);
        });
      }

      qtyInput.addEventListener('change', () => {
        const quantity = getLineQuantity(qtyInput.value, 0);
        scheduleLineQuantity(line, lineKey, lineNumber, quantity, root);
      });

      line.dataset.lineControlsBound = 'true';
    });

    root.querySelectorAll('[data-cart-remove]').forEach((button) => {
      if (button.dataset.removeBound === 'true') return;

      button.addEventListener('click', () => {
        const lineKey = button.getAttribute('data-line-key');
        if (!lineKey) return;
        const line = button.closest('[data-cart-line]');

        if (line) {
          scheduleLineQuantity(line, lineKey, line.getAttribute('data-line'), 0, root);
          return;
        }

        setCartBusyState(true);
        changeCartLineQuantity(lineKey, 0)
          .then(refreshCartSection)
          .catch((error) => {
            setCartBusyState(false);
            handleCartInteractionError(error);
          });
      });

      button.dataset.removeBound = 'true';
    });
  }

  function initCartInteractions(root) {
    if (!root) return;
    bindCartLineControls(root);
    bindCartNoteControls(root);
    refreshDiscountPresentation(root);
  }

  function handleCartInteractionError(error, options) {
    if (options && options.silent) return;
    if (document.querySelector('[data-cart-drawer].is-open')) {
      showDrawerCartError(error.message || 'Something went wrong. Please try again.');
      return;
    }
    window.alert(error.message || 'Something went wrong. Please try again.');
  }

  async function refreshCartSection(options) {
    const opts = options || {};
    const section = getRefreshableCartRoot();
    if (!section) return;

    const sectionId = section.getAttribute('data-section-id');
    if (!sectionId) {
      window.location.reload();
      return;
    }

    const wasDrawerOpen = opts.preserveDrawer || Boolean(document.querySelector('[data-cart-drawer].is-open'));
    setCartBusyState(true);

    try {
      let newSection = null;
      for (const path of getCartSectionRenderPaths()) {
        const response = await fetch(buildSectionRenderUrl(path, sectionId), { credentials: 'same-origin' });
        if (!response.ok) continue;

        newSection = parseCartSectionMarkup(await response.text());
        if (newSection) break;
      }
      if (!newSection) throw new Error('Could not refresh cart.');

      preserveDrawerOpenOnSection(newSection, wasDrawerOpen);
      section.replaceWith(newSection);
      initCartDrawerShell();
      initCartInteractions(newSection);

      const cart = await fetchCartJson();
      updateCartCountDisplay(cart.item_count);

      refreshDiscountPresentation(newSection);
    } catch (error) {
      handleCartInteractionError(error, { silent: opts.silentErrors });
      if (document.body.classList.contains('template-cart')) {
        window.location.reload();
      }
    } finally {
      setCartBusyState(false);
    }
  }

  function initCartDrawerShell() {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer || drawer.dataset.shellBound === 'true') return;

    drawer.querySelectorAll('[data-cart-drawer-close]').forEach((button) => {
      button.addEventListener('click', closeCartDrawer);
    });

    drawer.dataset.shellBound = 'true';
  }

  function initCartDrawerTriggers() {
    const openDrawerWithFreshCart = (event) => {
      if (!document.querySelector('[data-cart-drawer]')) return;
      event.preventDefault();
      showDrawerCartError('');
      openCartDrawer();
      setCartDrawerLoading(true);
      refreshCartSection({ preserveDrawer: true, silentErrors: true })
        .catch(() => {})
        .finally(() => {
          setCartDrawerLoading(false);
        });
    };

    document.querySelectorAll('[data-cart-drawer-open], [data-cart-link]').forEach((trigger) => {
      if (trigger.dataset.drawerTriggerBound === 'true') return;
      trigger.addEventListener('click', openDrawerWithFreshCart);
      trigger.dataset.drawerTriggerBound = 'true';
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && document.querySelector('[data-cart-drawer].is-open')) {
        closeCartDrawer();
      }
    });
  }

  function initCartDrawer() {
    const drawerRoot = getDrawerCartRoot();
    if (!drawerRoot) return;

    preloadCartDrawerSection();
    initCartDrawerShell();
    initCartDrawerTriggers();
    initCartInteractions(drawerRoot);

    document.addEventListener('agape:cart-discount-sync-request', () => {
      refreshCartSection({ silentErrors: true }).catch(() => {});
    });
  }

  async function addToCartWithDrawer(options) {
    const { variantId, quantity, sellingPlanId } = options;
    const normalizedQuantity = Math.max(1, parseInt(quantity, 10) || 1);
    const drawerRoot = getDrawerCartRoot();
    const hadDrawer = Boolean(drawerRoot);
    const previousCount = hadDrawer ? getDisplayedCartCount() : 0;

    showDrawerCartError('');

    if (hadDrawer) {
      // Show immediate progress while the cart section is re-rendered from Shopify.
      updateCartCountDisplay(previousCount + normalizedQuantity);
      setCartDrawerLoading(true);
      openCartDrawer();
    }

    try {
      await postAddToCart(variantId, normalizedQuantity, sellingPlanId);
      await refreshCartSection({ preserveDrawer: hadDrawer });
      if (!hadDrawer) {
        openCartDrawer();
      }
    } catch (error) {
      if (hadDrawer) {
        updateCartCountDisplay(previousCount);
      }
      showDrawerCartError(error.message || 'Could not add to cart. Please try again.');
      throw error;
    } finally {
      if (hadDrawer) {
        setCartDrawerLoading(false);
      }
    }
  }

  function initNoTouchHighlight() {
    const selector = [
      'a',
      '.flavor-slide',
      '.flavor-slide--link',
      '.shop-card',
      '.shop-card--link',
      '.review-card',
      '.stat-card',
      '.site-header__logo',
      '.site-header__cart',
      '.site-header__account',
      '.site-header__nav a',
      '.site-header__dropdown-toggle',
      '.mobile-nav-overlay__nav a',
      '.mobile-nav-stories',
      '[data-announcement-bar]',
      '.agape-product__related-card',
      '.agape-product__included-item'
    ].join(', ');

    document.addEventListener('mousedown', (event) => {
      if (event.button !== 0) return;
      if (event.target.closest('input, textarea, select, button, [role="button"], [contenteditable="true"]')) return;

      const interactive = event.target.closest(selector);
      if (interactive) {
        event.preventDefault();
      }
    });
  }

  function init() {
    initActiveDiscount();
    initCountdown();
    initAutoHideHeader();
    initMobileNav();
    initDesktopNavDropdown();
    initAnnouncementModal();
    initDiscountPresentation();
    initDelayedPrivacyBanner();
    initFadeUp();
    initCountUps();
    initStatCards();
    initHeroMobileLayout();
    initAnchorScroll();
    initTickerTracks();
    initCollectionVarietyPreload();
    initShopCardHoverGalleries();
    initCartDrawer();
    initNoTouchHighlight();
    resolveActiveDiscountFromCart().then(() => {
      applyAnnouncementDiscountState();
      refreshDiscountPresentation(document);
      notifyActiveDiscount();
      if (document.querySelector('[data-agape-cart]')) {
        document.dispatchEvent(new CustomEvent('agape:cart-discount-sync-request'));
      }
    });
    runWhenIdle(() => {
      document.dispatchEvent(new CustomEvent('agape:idle'));
    });
  }

  window.AgapeTheme = {
    animateCount,
    formatCount,
    runWhenIdle,
    primeSameOriginNavigation,
    getActiveDiscountCode,
    getActiveDiscountPercent,
    applyDiscountDisplayCents,
    formatMoneyCents,
    refreshDiscountPresentation,
    getDiscountPriceElement,
    resolveActiveDiscountFromCart,
    openCartDrawer,
    closeCartDrawer,
    refreshCartSection,
    addToCartWithDrawer,
    preloadCartDrawerSection,
    updateCartCountDisplay,
    initCartInteractions,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
